import type { CSSProperties } from 'react';
import type { IframePoolOrderItem } from '../../modules/api';
import { formatPrice } from '../../modules/currency';

export interface MyPacksStatsRowProps {
  inventory: IframePoolOrderItem[];
  packsOpened: number;
}

interface StatTheme {
  /** Design Constructor component name — `data-co-component` on the pill. */
  co: string;
  bg: string;
  border: string;
  radius: string;
  labelColor: string;
  valueColor: string;
}

// Per-card CSS var chains, written as string LITERALS (not templates) so
// both the design-constructor invariant scanner and Tailwind's static
// class extraction can see them. Chain: per-card knob → shared
// `MyPacksStatsBar.statMobileFill` (legacy all-cards fill) → the original
// Figma two-layer tint. The dark 0.18 base layer always renders — with a
// translucent legacy fill the pill reads a touch darker than the old
// whole-shorthand replacement, which is acceptable drift.
const STAT_THEMES: Record<'packsOpened' | 'estimatedValue' | 'buybackValue', StatTheme> = {
  packsOpened: {
    co: 'MyPacksStatPacksOpened',
    bg: 'linear-gradient(180deg, var(--co-MyPacksStatPacksOpened-bgFrom, var(--co-MyPacksStatsBar-statMobileFill, rgba(255,255,255,0.20))) 0%, var(--co-MyPacksStatPacksOpened-bgTo, var(--co-MyPacksStatsBar-statMobileFill, rgba(153,153,153,0.20))) 100%), linear-gradient(90deg, rgba(0,0,0,0.18) 0%, rgba(0,0,0,0.18) 100%)',
    border: 'var(--co-MyPacksStatPacksOpened-borderWidth, 0px) solid var(--co-MyPacksStatPacksOpened-borderColor, transparent)',
    radius: 'var(--co-MyPacksStatPacksOpened-borderRadius, 12px)',
    labelColor: 'var(--co-MyPacksStatPacksOpened-labelColor, inherit)',
    valueColor: 'var(--co-MyPacksStatPacksOpened-valueColor, inherit)',
  },
  estimatedValue: {
    co: 'MyPacksStatEstimatedValue',
    bg: 'linear-gradient(180deg, var(--co-MyPacksStatEstimatedValue-bgFrom, var(--co-MyPacksStatsBar-statMobileFill, rgba(255,255,255,0.20))) 0%, var(--co-MyPacksStatEstimatedValue-bgTo, var(--co-MyPacksStatsBar-statMobileFill, rgba(153,153,153,0.20))) 100%), linear-gradient(90deg, rgba(0,0,0,0.18) 0%, rgba(0,0,0,0.18) 100%)',
    border: 'var(--co-MyPacksStatEstimatedValue-borderWidth, 0px) solid var(--co-MyPacksStatEstimatedValue-borderColor, transparent)',
    radius: 'var(--co-MyPacksStatEstimatedValue-borderRadius, 12px)',
    labelColor: 'var(--co-MyPacksStatEstimatedValue-labelColor, inherit)',
    valueColor: 'var(--co-MyPacksStatEstimatedValue-valueColor, inherit)',
  },
  buybackValue: {
    co: 'MyPacksStatBuybackValue',
    bg: 'linear-gradient(180deg, var(--co-MyPacksStatBuybackValue-bgFrom, var(--co-MyPacksStatsBar-statMobileFill, rgba(255,255,255,0.20))) 0%, var(--co-MyPacksStatBuybackValue-bgTo, var(--co-MyPacksStatsBar-statMobileFill, rgba(153,153,153,0.20))) 100%), linear-gradient(90deg, rgba(0,0,0,0.18) 0%, rgba(0,0,0,0.18) 100%)',
    border: 'var(--co-MyPacksStatBuybackValue-borderWidth, 0px) solid var(--co-MyPacksStatBuybackValue-borderColor, transparent)',
    radius: 'var(--co-MyPacksStatBuybackValue-borderRadius, 12px)',
    labelColor: 'var(--co-MyPacksStatBuybackValue-labelColor, inherit)',
    valueColor: 'var(--co-MyPacksStatBuybackValue-valueColor, inherit)',
  },
};

/**
 * Three stats: packs opened, sum of fair-market values, sum of buyback
 * offers. Two responsive layouts:
 *   • Mobile (Figma 278:7388) — three full-width glass-frosted pills
 *     stacked vertically, label on the left, value on the right.
 *   • ≥600px — inline row separated by 3px dots (the old layout).
 */
export function MyPacksStatsRow({ inventory, packsOpened }: MyPacksStatsRowProps) {
  const totalInsured = sumBy(inventory, (i) => i.fairMarketValue ?? 0);
  const totalBuyback = sumBy(inventory, (i) => i.bestActiveOffer?.grossPrice ?? 0);

  const stats: { label: string; value: string; theme: StatTheme }[] = [
    { label: 'Packs Opened', value: String(packsOpened), theme: STAT_THEMES.packsOpened },
    {
      label: 'Total Estimated Value',
      value: formatPrice(totalInsured, 2),
      theme: STAT_THEMES.estimatedValue,
    },
    {
      label: 'Total Buyback Value',
      value: formatPrice(totalBuyback, 2),
      theme: STAT_THEMES.buybackValue,
    },
  ];

  // Typography slots live under the `MyPacksStatsBar` namespace (the
  // parent wrapper); each KPI pill additionally carries its own
  // `MyPacksStat*` namespace (see STAT_THEMES) so operators can theme
  // every card independently.
  return (
    <div
      className="
        flex w-full flex-col gap-2
        @[600px]:flex-row @[600px]:flex-wrap @[600px]:items-center @[600px]:gap-[14px]
      "
      style={{
        fontFamily: 'var(--co-MyPacksStatsBar-fontFamily, Inter), system-ui, sans-serif',
        color: 'var(--co-MyPacksStatsBar-valueColor, #FFFFFF)',
        fontSize: 'var(--co-MyPacksStatsBar-valueSize, 13px)',
        lineHeight: '20px',
      }}
    >
      {stats.map((s, i) => (
        <span key={s.label} className="contents">
          {i > 0 && (
            <span
              aria-hidden
              className="hidden size-[3px] shrink-0 rounded-full @[600px]:inline-block"
              style={{
                background:
                  'var(--co-MyPacksStatsBar-separatorColor, rgba(255,255,255,0.40))',
              }}
            />
          )}
          <Stat label={s.label} value={s.value} theme={s.theme} />
        </span>
      ))}
    </div>
  );
}

function Stat({ label, value, theme }: { label: string; value: string; theme: StatTheme }) {
  // Mobile (< 600px container): glass-frosted pill (Figma 278:7388) —
  // tinted background + blur. Desktop (≥ 600px): inline text pair, no
  // fill/blur. Mobile-only paint lives behind `@max-[600px]:` rather
  // than as the base + a `@[600px]:` override, because some of the
  // override utilities (`backdrop-blur-0`, arbitrary `[bg-image:none]`)
  // don't reliably outweigh their base counterparts in Tailwind 4 and
  // would leave the blur/tint visible on desktop.
  //
  // Surface theming is indirected through LOCAL `--stat-*` vars set
  // inline per card: Tailwind only ever sees the static
  // `[background:var(--stat-bg)]`-style classes, while the per-card
  // `--co-MyPacksStat*` chains live in STAT_THEMES. The `@max-[600px]:`
  // scoping keeps surface overrides from bleeding onto the desktop
  // inline-text layout; label/value colours apply at every width.
  const localVars = {
    '--stat-bg': theme.bg,
    '--stat-border': theme.border,
    '--stat-radius': theme.radius,
  } as CSSProperties; // safe: CSSProperties has no index signature for custom --* keys
  return (
    <div
      data-co-component={theme.co}
      className="
        relative flex w-full items-center justify-between gap-3
        @max-[600px]:[border-radius:var(--stat-radius)] @max-[600px]:px-4 @max-[600px]:py-2.5
        @max-[600px]:[background:var(--stat-bg)]
        @max-[600px]:[border:var(--stat-border)]
        @max-[600px]:backdrop-blur-[27px]
        @[600px]:w-auto @[600px]:gap-[5px] @[600px]:justify-start
        @[600px]:whitespace-nowrap
      "
      style={localVars}
    >
      <span style={{ color: theme.labelColor }}>
        <span className="@[600px]:hidden">{label}</span>
        <span className="hidden @[600px]:inline">{label}:</span>
      </span>
      <span style={{ color: theme.valueColor }}>{value}</span>
    </div>
  );
}

function sumBy<T>(items: T[], pick: (i: T) => number): number {
  return items.reduce((acc, item) => acc + (pick(item) || 0), 0);
}
